import type { EmailDraftRequest, HistoryItem } from '@/types/email-drafter'

const WEBHOOK_URL = import.meta.env.VITE_WEBHOOK_URL
const STORAGE_KEY = 'emailDrafterTester'

export class EmailDrafterService {
  private static instance: EmailDrafterService

  private constructor() {}

  static getInstance(): EmailDrafterService {
    if (!EmailDrafterService.instance) {
      EmailDrafterService.instance = new EmailDrafterService()
    }
    return EmailDrafterService.instance
  }

  async sendEmailDraft(request: EmailDraftRequest): Promise<string> {
    const startTime = Date.now()

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const htmlResponse = await response.text()
      const responseTime = Date.now() - startTime

      // Save to history
      this.addToHistory({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        request,
        response: htmlResponse,
        responseTime,
        status: 'success',
      })

      return htmlResponse
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'

      // Save error to history
      this.addToHistory({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        request,
        response: errorMessage,
        responseTime: Date.now() - startTime,
        status: 'error',
      })

      throw error
    }
  }

  // LocalStorage management
  saveToLocalStorage(key: string, data: any): void {
    try {
      localStorage.setItem(`${STORAGE_KEY}_${key}`, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
    }
  }

  loadFromLocalStorage<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(`${STORAGE_KEY}_${key}`)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.error('Failed to load from localStorage:', error)
      return null
    }
  }

  // History management
  getHistory(): HistoryItem[] {
    return this.loadFromLocalStorage<HistoryItem[]>('history') || []
  }

  addToHistory(item: HistoryItem): void {
    const history = this.getHistory()
    history.unshift(item)

    // Keep only last 50 items
    if (history.length > 50) {
      history.pop()
    }

    this.saveToLocalStorage('history', history)

    // Update stats
    this.updateStats(item.status === 'success', item.responseTime || 0)
  }

  clearHistory(): void {
    this.saveToLocalStorage('history', [])
    // Reset stats when clearing history
    this.saveToLocalStorage('stats', {
      totalTests: 0,
      successTests: 0,
      totalResponseTime: 0,
    })
  }

  deleteHistoryItem(id: string): void {
    const history = this.getHistory()
    const filtered = history.filter(item => item.id !== id)
    this.saveToLocalStorage('history', filtered)

    // Recalculate stats after deletion
    const stats = {
      totalTests: 0,
      successTests: 0,
      totalResponseTime: 0,
    }

    filtered.forEach(item => {
      stats.totalTests++
      if (item.status === 'success') {
        stats.successTests++
      }
      if (item.responseTime) {
        stats.totalResponseTime += item.responseTime
      }
    })

    this.saveToLocalStorage('stats', stats)
  }

  // Export/Import functionality
  exportData(): void {
    const data = {
      history: this.getHistory(),
      session: this.loadFromLocalStorage('session'),
      stats: this.loadFromLocalStorage('stats'),
      exportDate: new Date().toISOString(),
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `email_drafter_export_${new Date().getTime()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async importData(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string)

          if (data.history) {
            this.saveToLocalStorage('history', data.history)
          }
          if (data.session) {
            this.saveToLocalStorage('session', data.session)
          }
          if (data.stats) {
            this.saveToLocalStorage('stats', data.stats)
          }

          resolve()
        } catch (error) {
          reject(error)
        }
      }

      reader.onerror = () => reject(reader.error)
      reader.readAsText(file)
    })
  }

  // Stats management
  updateStats(success: boolean, responseTime: number): void {
    const stats = this.loadFromLocalStorage('stats') as any || {
      totalTests: 0,
      successTests: 0,
      totalResponseTime: 0,
    }

    stats.totalTests++
    if (success) stats.successTests++
    stats.totalResponseTime += responseTime

    this.saveToLocalStorage('stats', stats)
  }

  getStats() {
    const stats = this.loadFromLocalStorage('stats') as any || {
      totalTests: 0,
      successTests: 0,
      totalResponseTime: 0,
    }

    return {
      ...stats as any,
      averageResponseTime: stats.totalTests > 0
        ? Math.round(stats.totalResponseTime / stats.totalTests)
        : 0,
    }
  }
}

export const emailDrafterService = EmailDrafterService.getInstance()